/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-array-index-key */
import I18n from 'i18n-js';
import React, { useEffect, useMemo, useState } from 'react';
import { OfertaRegistroAPI } from 'types/models/OfertasServiceNuevoOnboarding';
import ofertasService from 'services/remote/OfertasServiceNuevoOnboarding';
import Modal from 'components/Modal/Modal';
import styles from './OfertaForm.module.scss';
import OfertaRow from './OfertaRow';

interface OfertaFormProps {
  initialData: Partial<OfertaRegistroAPI>[];
  onChange: (data: Partial<OfertaRegistroAPI>[]) => void;
  readOnly?: boolean;
  solicitudId: string;
}

const OfertaForm = ({ solicitudId, initialData = [{}], onChange }: OfertaFormProps) => {
  const [data, setData] = useState(initialData);
  const [viewIdx, setViewIdx] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const selectedRow = useMemo(() => (viewIdx != null ? data[viewIdx] : null), [viewIdx, data]);
  const camposOferta: { key: keyof OfertaRegistroAPI; label: string }[] = [
    { key: 'origen_de_datos', label: I18n.t('Oferta.origen_de_datos') },
    { key: 'modo_de_acceso', label: I18n.t('Oferta.modo_de_acceso') },
    { key: 'generacion_assignada', label: I18n.t('Oferta.generacion_assignada') },
    { key: 'importe_anual_sin_autoconsumo', label: I18n.t('Oferta.importe_anual_sin_autoconsumo') },
    { key: 'ahorro_anual_total', label: I18n.t('Oferta.ahorro_anual_total') },
    { key: 'ahorro_anual_real', label: I18n.t('Oferta.ahorro_anual_real') },
    { key: 'precio_de_acceso', label: I18n.t('Oferta.precio_de_acceso') },
    { key: 'percentaje_facturacion', label: I18n.t('Oferta.percentaje_facturacion') },
    { key: 'ahorro_anual_co2', label: I18n.t('Oferta.ahorro_anual_co2') },
    { key: 'arboles_plantados', label: I18n.t('Oferta.arboles_plantados') },
    { key: 'potencia_nominal_assignada', label: I18n.t('Oferta.potencia_nominal_assignada') },
    { key: 'duracion_del_contrato', label: I18n.t('Oferta.duracion_del_contrato') },
    { key: 'ahorro_mensual_promedio', label: I18n.t('Oferta.ahorro_mensual_promedio') },
    { key: 'precio_mensual_promedio', label: I18n.t('Oferta.precio_mensual_promedio') },
    { key: 'excedentes', label: I18n.t('Oferta.excedentes') },
    { key: 'coeficiente_reparto', label: I18n.t('Oferta.coeficiente_reparto') },
    { key: 'pedir_iban', label: I18n.t('Oferta.pedir_iban') },
  ];

  // Agrega una fila vacía si la última tiene datos
  useEffect(() => {
    const ultima = data[data.length - 1];
    const estaVacia = !ultima || Object.values(ultima).every((v) => v === undefined || v === null || v === '');

    if (!estaVacia) {
      const nueva = [...data, {}];
      setData(nueva);
      onChange(nueva);
    }
  }, [data, onChange]);

  const handleRowChange = (idx: number, updated: Partial<OfertaRegistroAPI>) => {
    const nuevas = [...data];
    nuevas[idx] = updated;
    setData(nuevas);
    onChange(nuevas);
  };

  const handleDelete = async (idx: number) => {
    const oferta = data[idx];

    const confirmed = window.confirm('¿Estás seguro de que quieres eliminar esta oferta?');
    if (!confirmed) return;

    if (oferta.id) {
      try {
        await ofertasService.deleteOferta(solicitudId, oferta.id.toString());
      } catch (err) {
        console.error('Error al eliminar oferta', err);
        alert('Error al eliminar la oferta.');
        return;
      }
    }

    const nuevas = data.filter((_, i) => i !== idx);
    setData(nuevas);
    onChange(nuevas);
  };

  const MODO_MAP: Record<number, string> = {
    0: 'Ahorra siempre',
    1: 'Derecho exclusivo',
    2: 'Venta de energía',
  };

  const EXCEDENTES_MAP: Record<number, string> = {
    0: 'Sin excedentes',
    1: 'Con compensación',
    2: 'Sin compensación',
  };

  const toNum = (v: any) => {
    if (v === null || v === undefined || v === '') return NaN;
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : NaN;
  };

  const fmtMoney = (v: any) => {
    const n = toNum(v);
    return Number.isFinite(n) ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n) : '—';
  };

  const fmtPercent = (v: any) => {
    const n = toNum(v);
    return Number.isFinite(n) ? `${n}%` : '—';
  };

  const fmtNumber = (v: any, maxFrac = 3) => {
    const n = toNum(v);
    return Number.isFinite(n) ? new Intl.NumberFormat('es-ES', { maximumFractionDigits: maxFrac }).format(n) : '—';
  };

  const fmtBool = (v: any) =>
    v === true || v === 'true' || v === 1 || v === '1'
      ? 'Sí'
      : v === false || v === 'false' || v === 0 || v === '0'
      ? 'No'
      : '—';

  const fmtMonths = (v: any) => {
    const n = toNum(v);
    if (!Number.isFinite(n)) return '—';
    return n === 1 ? '1 mes' : `${n} meses`;
  };

  // Router de formato por clave
  const formatByKey = (key: string, value: any): string => {
    switch (key) {
      case 'modo_de_acceso': {
        const n = Math.trunc(toNum(value));
        return MODO_MAP[n] ?? '—';
      }
      case 'generacion_assignada':
      case 'potencia_nominal_assignada':
      case 'coeficiente_reparto':
        return fmtNumber(value);

      case 'importe_anual_sin_autoconsumo':
      case 'ahorro_anual_total':
      case 'ahorro_anual_real':
      case 'precio_de_acceso':
      case 'ahorro_mensual_promedio':
      case 'precio_mensual_promedio':
        return fmtMoney(value);

      case 'percentaje_facturacion':
        return fmtPercent(value);

      case 'ahorro_anual_co2':
      case 'arboles_plantados':
        return fmtNumber(value, 0);

      case 'duracion_del_contrato':
        return fmtMonths(value);

      case 'excedentes': {
        const n = Math.trunc(toNum(value));
        return EXCEDENTES_MAP[n] ?? '—';
      }

      case 'pedir_iban':
        return fmtBool(value);

      default:
        return value === null || value === undefined || value === '' ? '—' : String(value);
    }
  };

  return (
    <div className={styles.excelTable}>
      <table>
        <thead>
          <tr>
            {camposOferta.map((campo) => (
              <th className={styles.title} key={campo.key}>
                {campo.label}
              </th>
            ))}
            <th key="delete" />
          </tr>
        </thead>
        <tbody className={styles.t_body}>
          {' '}
          {data.map((fila, idx) => (
            <OfertaRow
              key={idx}
              data={fila}
              campos={camposOferta}
              onChange={(updated) => handleRowChange(idx, updated)}
              onDelete={() => handleDelete(idx)}
              onView={() => {
                setViewIdx(idx);
                setOpen(true);
              }}
            />
          ))}
        </tbody>
      </table>

      {/* MODAL DETALLE */}
      {open ? (
        <Modal
          isOpen={open}
          title="Detalle de la oferta"
          onClick2={() => {
            setOpen(false);
            setViewIdx(null);
          }}
          onClose={() => {
            setOpen(false);
            setViewIdx(null);
          }}
          body={
            selectedRow ? (
              <div className={styles.detailWrapper}>
                <table className={styles.detailTable}>
                  <tbody>
                    {camposOferta.map((c) => (
                      <tr key={String(c.key)}>
                        <td className={styles.detailLabel}>{c.label}</td>
                        <td className={styles.detailValue}>
                          {formatByKey(String(c.key), (selectedRow as any)[c.key])}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null
          }
        />
      ) : null}
    </div>
  );
};

export default OfertaForm;
